import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { createReceivableInvoice, fetchReceivables, postReceivableInvoice, receiveReceivablePayment } from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const AR_STATUS = {
  draft: { label: "Draft", color: "secondary" },
  open: { label: "Open", color: "info" },
  partial: { label: "Partial", color: "warning" },
  paid: { label: "Paid", color: "success" },
  void: { label: "Void", color: "dark" },
};

const AccountsReceivablePage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    invoice_number: "",
    invoice_date: todayIsoDate(),
    due_date: todayIsoDate(),
    amount: "",
    description: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchReceivables(branchId).then(setRows), { errorMessage: "Failed to load receivables." })
      .catch((err) => setError(formatApiError(err, "Failed to load receivables.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const loadRef = useLatestRef(load);

  const action = useCallback(
    (row, type) => {
      confirm({
        title: type === "post" ? "Post invoice?" : "Record receipt?",
        message: type === "post" ? "Invoice becomes open." : "Apply full remaining balance.",
        confirmLabel: type === "post" ? "Post" : "Receive",
        successMessage: type === "post" ? "Invoice posted." : "Payment received.",
        onConfirm: async () => {
          if (type === "post") await postReceivableInvoice(row.id);
          else await receiveReceivablePayment(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Invoice #", selector: (r) => r.invoice_number, width: "110px" },
      { name: "Customer", selector: (r) => r.customer_name, grow: 2 },
      { name: "Due", cell: (r) => formatDate(r.due_date), width: "110px" },
      { name: "Amount", cell: (r) => formatTZS(r.amount), right: true, width: "130px" },
      { name: "Balance", cell: (r) => formatTZS(r.balance_due), right: true, width: "130px" },
      {
        name: "Status",
        cell: (r) => {
          const m = AR_STATUS[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={m.color}>{m.label}</Badge>;
        },
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              r.status === "draft" && {
                icon: "check",
                label: "Post invoice",
                onClick: () => action(r, "post"),
                variant: "primary",
              },
              (r.status === "open" || r.status === "partial") && {
                icon: "collection",
                label: "Receive payment",
                onClick: () => action(r, "receive"),
                variant: "success",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [action]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () => createReceivableInvoice({
          branch_id: branchId,
          customer_name: form.customer_name,
          invoice_number: form.invoice_number,
          invoice_date: form.invoice_date,
          due_date: form.due_date,
          amount: Number(form.amount),
          description: form.description,
        }),
        { successMessage: "Invoice created." }
      );
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Accounts Receivable" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Receivable invoices</h5>
            <div className="d-flex gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>New invoice</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New receivable invoice</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6"><FormGroup><Label>Customer</Label>
                  <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Invoice number</Label>
                  <Input required value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Invoice date</Label>
                  <Input type="date" required value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Due date</Label>
                  <Input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Amount</Label>
                  <Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></FormGroup></Col>
                <Col md="12"><FormGroup><Label>Description</Label>
                  <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormGroup></Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save draft"}</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default AccountsReceivablePage;
